import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BiChevronLeft } from "react-icons/bi";
import Spin from "shared/components/Spin";
import Header from "shared/components/Header";
import { useQuery, useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { SCREEN } from "utils/api/graphql/queries/screens";
import { SCREENS } from "utils/constants";
import { ensureDraftTarget } from "utils/helpers/screenDraft";
import VersionList from "./VersionList";
import ScreenPreview from "./ScreenPreview";
import SlotInspector from "./SlotInspector";
import SlotList from "./SlotList";
import BindingReview, { isPublishBlocked } from "./BindingReview";

const Detail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const { data, loading, refetch } = useQuery(SCREEN, {
    variables: { where: { id } },
    onCompleted: (result) => {
      const screen = result?.screen;
      if (!selectedVersionId && screen?.versions?.length > 0) {
        setSelectedVersionId(screen.versions[0].id);
      }
    },
  });

  const [detectSlots, { loading: detecting }] = useMutation(
    graphql.mutations.DETECT_SLOTS,
    {
      onCompleted: () => refetch(),
    },
  );

  const [publishVersion, { loading: publishing }] = useMutation(
    graphql.mutations.PUBLISH_SCREEN_VERSION,
    {
      onCompleted: () => refetch(),
    },
  );

  const [updateManifest] = useMutation(
    graphql.mutations.UPDATE_BINDING_MANIFEST,
  );

  const [ensureScreenDraft] = useMutation(
    graphql.mutations.ENSURE_SCREEN_DRAFT,
  );

  if (loading) return <Spin />;

  const screen = data?.screen;
  if (!screen) return null;

  const versions = screen.versions || [];
  const activeVersion =
    versions.find((v) => v.id === selectedVersionId) || versions[0];
  const manifest = activeVersion?.bindingManifest;
  const slots = manifest?.slots || [];
  const report = manifest?.report || null;
  const publishBlocked = isPublishBlocked(report);

  const boundCount = slots.filter((s) => s.binding?.kind !== "unbound").length;
  const totalCount = slots.length;
  const processId = screen.status?.process?.id;

  const writableVersion = () =>
    ensureDraftTarget({
      ensureScreenDraft,
      screen: { id: screen.id },
      version: activeVersion,
    })
      .then(({ version }) => {
        if (version.id !== activeVersion.id) setSelectedVersionId(version.id);
        return version;
      })
      .catch(() => null);

  const handleSlotUpdate = async (slotId, binding) => {
    if (!manifest) return;
    const version = await writableVersion();
    if (!version) return;
    const updatedSlots = (version.bindingManifest?.slots || []).map((s) =>
      s.id === slotId ? { ...s, binding } : s,
    );
    updateManifest({
      variables: {
        where: { id: version.bindingManifest.id },
        data: { slots: updatedSlots },
      },
      onCompleted: () => refetch(),
    });
  };

  const handleDetect = async () => {
    const version = await writableVersion();
    if (!version) return;
    detectSlots({ variables: { screenVersion: { id: version.id } } });
  };

  return (
    <>
      <Header
        title={screen.status?.name}
        subtitle={`${boundCount}/${totalCount} slots liés`}
        actions={[
          {
            text: "Retour",
            onClick: () =>
              navigate(SCREENS.PRIVATE.ADMIN.SCREEN_STUDIOS.LIST.path),
            type: "gray--link",
            icon: <BiChevronLeft size={20} />,
          },
          {
            text: "Détecter les slots",
            onClick: handleDetect,
            type: "primary--outlined",
            loading: detecting,
            disabled: !activeVersion,
          },
          {
            text: "Publier",
            onClick: () =>
              publishVersion({
                variables: { where: { id: activeVersion?.id } },
              }),
            type: "primary",
            loading: publishing,
            disabled:
              !activeVersion ||
              activeVersion.status === "published" ||
              publishBlocked,
          },
        ]}
      />

      <div className="screen-studio">
        <div className="screen-studio__sidebar">
          <VersionList
            versions={versions}
            activeVersionId={selectedVersionId}
            onSelect={setSelectedVersionId}
          />
          <SlotList
            slots={slots}
            selectedSlot={selectedSlot}
            onSelect={setSelectedSlot}
            processId={processId}
          />
          <BindingReview report={report} />
        </div>

        <div className="screen-studio__preview">
          <ScreenPreview
            htmlContent={activeVersion?.htmlContent}
            styleContent={activeVersion?.styleContent}
            slots={slots}
            selectedSlot={selectedSlot}
            onSlotSelect={setSelectedSlot}
          />
        </div>

        <div className="screen-studio__inspector">
          <SlotInspector
            slot={
              selectedSlot ? slots.find((s) => s.id === selectedSlot) : null
            }
            onSlotUpdate={handleSlotUpdate}
            onClose={() => setSelectedSlot(null)}
            processId={processId}
          />
        </div>
      </div>
    </>
  );
};

export default Detail;
